import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';

/**
 * usePermissions Composable
 *
 * Provides permission and role checking functionality for Vue components.
 * Integrates with Inertia.js shared props to access current user's permissions and roles.
 *
 * Usage:
 * ```js
 * import { usePermissions } from '@/Composables/usePermissions';
 *
 * const { can, hasRole, isAdmin } = usePermissions();
 *
 * if (can('system.roles.manage')) {
 *   // Show roles management UI
 * }
 * ```
 *
 * Phase 9: Roles & Permissions Management UI
 */
export function usePermissions() {
  const page = usePage();

  /**
   * Get current user's permissions from Inertia shared props.
   * Returns array of permission names (e.g., ['system.roles.manage', 'tickets.view'])
   *
   * @returns {ComputedRef<string[]>}
   */
  const permissions = computed(() => page.props.auth?.permissions || []);

  /**
   * Get current user's roles from Inertia shared props.
   * Returns array of role names (e.g., ['System Administrator', 'Agent'])
   *
   * @returns {ComputedRef<string[]>}
   */
  const roles = computed(() => page.props.auth?.roles || []);

  /**
   * Check if the current user has a specific permission.
   *
   * @param {string} permission - Permission name to check (e.g., 'system.roles.manage')
   * @returns {boolean} - True if user has the permission
   *
   * @example
   * can('system.roles.manage') // true or false
   */
  const can = (permission) => {
    if (!permission || typeof permission !== 'string') {
      return false;
    }
    return permissions.value.includes(permission);
  };

  /**
   * Check if the current user has ANY of the specified permissions.
   * Returns true if user has at least one of the permissions.
   *
   * @param {string[]} permissionArray - Array of permission names
   * @returns {boolean} - True if user has at least one permission
   *
   * @example
   * canAny(['system.roles.manage', 'system.users.manage']) // true if has either
   */
  const canAny = (permissionArray) => {
    if (!Array.isArray(permissionArray) || permissionArray.length === 0) {
      return false;
    }
    return permissionArray.some(permission => can(permission));
  };

  /**
   * Check if the current user has ALL of the specified permissions.
   * Returns true only if user has every permission in the array.
   *
   * @param {string[]} permissionArray - Array of permission names
   * @returns {boolean} - True if user has all permissions
   *
   * @example
   * canAll(['tickets.view', 'tickets.create']) // true only if has both
   */
  const canAll = (permissionArray) => {
    if (!Array.isArray(permissionArray) || permissionArray.length === 0) {
      return false;
    }
    return permissionArray.every(permission => can(permission));
  };

  /**
   * Check if the current user has a specific role.
   *
   * @param {string} role - Role name to check (e.g., 'System Administrator')
   * @returns {boolean} - True if user has the role
   *
   * @example
   * hasRole('System Administrator') // true or false
   */
  const hasRole = (role) => {
    if (!role || typeof role !== 'string') {
      return false;
    }
    return roles.value.includes(role);
  };

  /**
   * Check if the current user has ANY of the specified roles.
   * Returns true if user has at least one of the roles.
   *
   * @param {string[]} roleArray - Array of role names
   * @returns {boolean} - True if user has at least one role
   *
   * @example
   * hasAnyRole(['System Administrator', 'Agent']) // true if has either
   */
  const hasAnyRole = (roleArray) => {
    if (!Array.isArray(roleArray) || roleArray.length === 0) {
      return false;
    }
    return roleArray.some(role => hasRole(role));
  };

  /**
   * Check if current user is an administrator.
   * Returns true if user has 'System Administrator' or 'Company Subscriber' role.
   *
   * @returns {ComputedRef<boolean>}
   *
   * @example
   * if (isAdmin.value) {
   *   // Show admin features
   * }
   */
  const isAdmin = computed(() => {
    return hasAnyRole(['System Administrator', 'Company Subscriber']);
  });

  /**
   * Check if current user is a super admin (Company Subscriber).
   * Company Subscribers have the highest level of access.
   *
   * @returns {ComputedRef<boolean>}
   *
   * @example
   * if (isSuperAdmin.value) {
   *   // Show super admin only features
   * }
   */
  const isSuperAdmin = computed(() => {
    return hasRole('Company Subscriber');
  });

  /**
   * Check if current user has any permissions at all.
   * Useful for checking if permission system is loaded.
   *
   * @returns {ComputedRef<boolean>}
   */
  const hasPermissions = computed(() => {
    return permissions.value.length > 0;
  });

  /**
   * Check if current user has any roles at all.
   * Useful for checking if user is properly configured.
   *
   * @returns {ComputedRef<boolean>}
   */
  const hasRoles = computed(() => {
    return roles.value.length > 0;
  });

  /**
   * Get count of user's permissions.
   *
   * @returns {ComputedRef<number>}
   */
  const permissionCount = computed(() => {
    return permissions.value.length;
  });

  /**
   * Get count of user's roles.
   *
   * @returns {ComputedRef<number>}
   */
  const roleCount = computed(() => {
    return roles.value.length;
  });

  return {
    // Reactive data
    permissions,
    roles,

    // Permission checking methods
    can,
    canAny,
    canAll,

    // Role checking methods
    hasRole,
    hasAnyRole,

    // Computed shortcuts
    isAdmin,
    isSuperAdmin,
    hasPermissions,
    hasRoles,
    permissionCount,
    roleCount,
  };
}
